/**
 * Validation utilities for workplace settings
 * Handles file validation, color validation, and other input validation
 */

/**
 * Safely parse an integer, returning a fallback for invalid inputs
 * Guards against NaN from empty strings or invalid values
 */
export const safeParseInt = (
  value: string,
  fallback: number = 0,
  minValue: number = 0
): number => {
  if (value === '' || value === null || value === undefined) {
    return fallback;
  }
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    return fallback;
  }
  return Math.max(minValue, parsed);
};

/**
 * File validation configuration for logo uploads
 */
export const LOGO_UPLOAD_CONFIG = {
  maxSizeBytes: 2 * 1024 * 1024, // 2MB
  allowedTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
  allowedExtensions: ['.png', '.jpg', '.jpeg', '.gif'],
  maxDimensions: {
    width: 2000,
    height: 2000
  }
} as const;

/**
 * Validation result interface
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate a file for logo upload
 */
export const validateLogoFile = (file: File): ValidationResult => {
  // Check file size
  if (file.size > LOGO_UPLOAD_CONFIG.maxSizeBytes) {
    return {
      isValid: false,
      error: `File size must be less than ${LOGO_UPLOAD_CONFIG.maxSizeBytes / (1024 * 1024)}MB`
    };
  }

  // Check file type
  if (!LOGO_UPLOAD_CONFIG.allowedTypes.includes(file.type as any)) {
    return {
      isValid: false,
      error: `File type must be one of: ${LOGO_UPLOAD_CONFIG.allowedTypes.join(', ')}`
    };
  }

  // Check file extension
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  if (!LOGO_UPLOAD_CONFIG.allowedExtensions.includes(extension as any)) {
    return {
      isValid: false,
      error: `File extension must be one of: ${LOGO_UPLOAD_CONFIG.allowedExtensions.join(', ')}`
    };
  }

  return { isValid: true };
};

/**
 * Validate image dimensions
 */
export const validateImageDimensions = (file: File): Promise<ValidationResult> => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      
      if (img.width > LOGO_UPLOAD_CONFIG.maxDimensions.width || 
          img.height > LOGO_UPLOAD_CONFIG.maxDimensions.height) {
        resolve({
          isValid: false,
          error: `Image dimensions must be less than ${LOGO_UPLOAD_CONFIG.maxDimensions.width}x${LOGO_UPLOAD_CONFIG.maxDimensions.height}px`
        });
      } else {
        resolve({ isValid: true });
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({
        isValid: false,
        error: 'Invalid image file'
      });
    };

    img.src = url;
  });
};

/**
 * Validate hex color format
 */
export const validateHexColor = (color: string): ValidationResult => {
  const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
  
  if (!color) {
    return {
      isValid: false,
      error: 'Color is required'
    };
  }

  if (!hexColorRegex.test(color)) {
    return {
      isValid: false,
      error: 'Color must be a valid hex color (e.g., #FF5733)'
    };
  }

  return { isValid: true };
};

/**
 * Validate business name
 */
export const validateBusinessName = (name: string): ValidationResult => {
  if (!name || !name.trim()) {
    return {
      isValid: false,
      error: 'Business name is required'
    };
  }

  if (name.trim().length < 2) {
    return {
      isValid: false,
      error: 'Business name must be at least 2 characters long'
    };
  }

  if (name.trim().length > 100) {
    return {
      isValid: false,
      error: 'Business name must be less than 100 characters'
    };
  }

  return { isValid: true };
};

/**
 * Validate country code (ISO 3166-1 alpha-2)
 */
export const validateCountryCode = (code: string): ValidationResult => {
  if (!code) {
    return {
      isValid: false,
      error: 'Country code is required'
    };
  }

  if (code.length !== 2) {
    return {
      isValid: false,
      error: 'Country code must be a 2-character ISO 3166-1 alpha-2 code'
    };
  }

  if (!/^[A-Z]{2}$/.test(code)) {
    return {
      isValid: false,
      error: 'Country code must contain only uppercase letters'
    };
  }

  return { isValid: true };
};

/**
 * Validate currency code (ISO 4217)
 */
export const validateCurrencyCode = (code: string): ValidationResult => {
  if (!code) {
    return {
      isValid: false,
      error: 'Currency code is required'
    };
  }

  if (code.length !== 3) {
    return {
      isValid: false,
      error: 'Currency code must be a 3-character ISO 4217 code'
    };
  }

  if (!/^[A-Z]{3}$/.test(code)) {
    return {
      isValid: false,
      error: 'Currency code must contain only uppercase letters'
    };
  }

  return { isValid: true };
};

/**
 * Validate time format
 */
export const validateTimeFormat = (format: string): ValidationResult => {
  const validFormats = ['12h', '24h'];
  
  if (!format) {
    return {
      isValid: false,
      error: 'Time format is required'
    };
  }

  if (!validFormats.includes(format)) {
    return {
      isValid: false,
      error: 'Time format must be either 12h or 24h'
    };
  }

  return { isValid: true };
};

/**
 * Validate date format
 */
export const validateDateFormat = (format: string): ValidationResult => {
  const validFormats = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
  
  if (!format) {
    return {
      isValid: false,
      error: 'Date format is required'
    };
  }

  if (!validFormats.includes(format)) {
    return {
      isValid: false,
      error: 'Date format must be DD/MM/YYYY, MM/DD/YYYY, or YYYY-MM-DD'
    };
  }

  return { isValid: true };
};

/**
 * Comprehensive validation for all workplace details
 */
export interface WorkplaceDetailsValidation {
  businessName?: ValidationResult;
  timeFormat?: ValidationResult;
  dateFormat?: ValidationResult;
  countryCode?: ValidationResult;
  currencyCode?: ValidationResult;
  themeColor?: ValidationResult;
  logoFile?: ValidationResult;
}

/**
 * Validate all workplace details at once
 */
export const validateWorkplaceDetails = (data: {
  businessName?: string;
  timeFormat?: string;
  dateFormat?: string;
  countryCode?: string;
  currencyCode?: string;
  themeColor?: string;
  logoFile?: File;
}): WorkplaceDetailsValidation => {
  const validation: WorkplaceDetailsValidation = {};

  if (data.businessName !== undefined) {
    validation.businessName = validateBusinessName(data.businessName);
  }

  if (data.timeFormat !== undefined) {
    validation.timeFormat = validateTimeFormat(data.timeFormat);
  }

  if (data.dateFormat !== undefined) {
    validation.dateFormat = validateDateFormat(data.dateFormat);
  }

  if (data.countryCode !== undefined) {
    validation.countryCode = validateCountryCode(data.countryCode);
  }

  if (data.currencyCode !== undefined) {
    validation.currencyCode = validateCurrencyCode(data.currencyCode);
  }

  if (data.themeColor !== undefined) {
    validation.themeColor = validateHexColor(data.themeColor);
  }

  if (data.logoFile !== undefined) {
    validation.logoFile = validateLogoFile(data.logoFile);
  }

  return validation;
};

/**
 * Check if validation results contain any errors
 */
export const hasValidationErrors = (validation: WorkplaceDetailsValidation): boolean => {
  return Object.values(validation).some(result => result && !result.isValid);
};

/**
 * Get the first validation error message
 */
export const getFirstValidationError = (validation: WorkplaceDetailsValidation): string | null => {
  for (const result of Object.values(validation)) {
    if (result && !result.isValid && result.error) {
      return result.error;
    }
  }
  return null;
};
